import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, PencilSimple } from '@phosphor-icons/react';
import SearchableSelect from '../../components/SearchableSelect';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

function StatusBadge({ status }) {
  return <span className={`text-xs uppercase tracking-widest px-2 py-1 ${status === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF8C00]/10 text-[#FF8C00]'}`}>{status}</span>;
}

export default function HelperContractors() {
  const [items, setItems] = useState([]);
  const [haltNames, setHaltNames] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', halt_location: '' });

  useEffect(() => { fetchAll(); fetchHalts(); }, []);
  const fetchAll = async () => { try { const { data } = await api.get('/admin/helper-contractors'); setItems(data); } catch {} };
  const fetchHalts = async () => {
    try { const { data } = await api.get('/admin/halt-locations'); setHaltNames(Array.isArray(data) ? data : []); }
    catch { setHaltNames([]); }
  };

  const resetForm = () => { setForm({ name: '', mobile: '', halt_location: '' }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/helper-contractors/${editing.id}`, form);
      else await api.post('/admin/helper-contractors', form);
      fetchAll(); setDialogOpen(false); resetForm();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const toggleStatus = async (rec) => {
    try { await api.put(`/admin/toggle-status/helper-contractors/${rec.id}`); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.mobile || '').toLowerCase().includes(q) ||
        (c.halt_location || '').toLowerCase().includes(q))
    : items;

  return (
    <div data-testid="helper-contractors-page" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Helper Contractors</h1>

      <div className="flex items-center gap-3 justify-between">
        <Input
          data-testid="contractors-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, mobile or halt location..."
          className="rounded-none max-w-md"
        />
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-contractor-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}>
              <Plus size={16} className="mr-2" />Add Contractor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit Contractor' : 'Add Contractor'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Contractor Name *</Label>
                <Input data-testid="contractor-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="rounded-none" autoFocus />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Mobile *</Label>
                <Input
                  data-testid="contractor-mobile-input"
                  value={form.mobile}
                  onChange={e => setForm({ ...form, mobile: e.target.value.replace(/[^0-9+]/g, '') })}
                  required maxLength={15}
                  className="rounded-none"
                  placeholder="10-digit mobile number"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Halt Location</Label>
                <SearchableSelect
                  value={form.halt_location}
                  onChange={v => setForm({ ...form, halt_location: v })}
                  options={haltNames.map(n => ({ value: n, label: n }))}
                  placeholder="Select halt location"
                  searchPlaceholder="Search halt location..."
                  emptyText="No halt locations"
                  allowClear
                  testId="contractor-halt-select"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button>
                <Button data-testid="contractor-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-[#666] text-xs">{q ? `${filtered.length} match of ${items.length}` : `${items.length} contractors`}</p>

      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Mobile</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c, i) => (
              <TableRow key={c.id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-sm">{c.mobile || <span className="text-[#999]">—</span>}</TableCell>
                <TableCell>{c.halt_location || <span className="text-[#999]">—</span>}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => { setEditing(c); setForm({ name: c.name, mobile: c.mobile || '', halt_location: c.halt_location || '' }); setDialogOpen(true); }}
                    className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-contractor-${c.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={() => toggleStatus(c)}
                    className="px-2 py-1 text-xs uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] ml-1"
                    data-testid={`toggle-contractor-${c.id}`}>
                    {c.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                  <SuperAdminDeleteButton masterType="helper-contractors" recordId={c.id} recordName={c.name} onDeleted={fetchAll} />
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#666] py-8">{q ? 'No contractors match your search' : 'No contractors yet'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
